// screen: Players → TV Preview ("תצוגה בטלוויזיה"), step 1 (2026-10-14): the TV Lab's emulators running the ViewStream TV
// app — live screens, a remote per device (keyboard shortcuts too), relaunch with the launch → first-frame timer,
// screenshots, recordings and the device log. One driver at a time: "Take control" holds the lab for 10 minutes
// after the last action; everyone else watches. Shown to Interhost operators and tenants with flag tvlab.preview.
// The selected device and full screen play live video with sound over WebRTC (2026-10-15); the other tiles, and any
// network where WebRTC can't connect, show the screen pictures.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import {
  ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, Download, FastForward, Home, Pause, Play, Rewind,
  RotateCw, Camera, Square, Undo2, Volume1, Volume2, VolumeX, Hand, LogOut, Maximize2, Minimize2, Radio,
} from 'lucide-react';
import { Badge, Button, EmptyState, Skeleton, useToast } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { TVLabSetup } from '~/app/TVLabSetup';
import { TenantAnalyticsLink } from '~/app/TenantAnalytics';
import type { ApiError } from '@viewstream/api-client';
import {
  COLOUR_KEYS, DIGIT_KEYS, fetchTVLabLogs, fileUrl, hasHomeKey, hasVolumeKeys, keyFromKeyboard, mediaKeys, tapPoint, useLiveVideo, useScreen,
  isLabEditingKey, useLabFullScreen, useTVLab, useTVLabAction, useTVLabFiles, useTVLabLease, type TVLabDevice, type TVLabLogLine,
  labView, supportsLabAction, supportsLabKey, useVendorVideo,
} from '~/app/tvlab';

export const Route = createFileRoute('/_layout/t/$slug/players/tv')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`TV Preview`); }, [t]);
  const lab = useTVLab(tenant.slug);
  const st = lab.data;
  const devices = React.useMemo(() => st?.devices ?? [], [st?.devices]);
  const [selId, setSelId] = React.useState<string | null>(null);
  const sel = devices.find((d) => d.id === selId) ?? devices[0];
  const canDrive = can(scopes, 'assets:write') || can(scopes, 'channels:operate');
  const mine = !!st?.lease?.mine;
  const [fsId, setFsId] = React.useState<string | null>(null);
  const fs = devices.find((d) => d.id === fsId);
  const [sound, setSound] = React.useState(false); // the live video's sound (selected device / full screen)
  const [cinemaId, setCinemaId] = React.useState<string | null>(null);
  const cinema = !!sel && cinemaId === sel.id;
  const previewRef = React.useRef<HTMLButtonElement>(null);
  const cinemaArea = React.useRef<HTMLDivElement>(null);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const wasCinema = React.useRef(false);
  const enterCinema = () => {
    if (!sel) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCinemaId(sel.id);
  };
  const exitCinema = () => setCinemaId(null);
  React.useLayoutEffect(() => {
    if (cinema) {
      previewRef.current?.focus({ preventScroll: true });
      cinemaArea.current?.scrollIntoView({ block: 'start' });
    } else if (wasCinema.current && returnFocus.current?.isConnected) {
      returnFocus.current.focus({ preventScroll: true });
    }
    wasCinema.current = cinema;
  }, [cinema]);

  const back = (
    <Link to={'/t/$slug/players' as never} params={{ slug: tenant.slug } as never} className="mb-2 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
      <ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Players`}
    </Link>
  );
  if (lab.isPending) return <div>{back}<Skeleton className="h-96" /></div>;
  if (!st?.available) {
    return (
      <div>{back}
        <EmptyState title={t`TV Preview is not available for this account`} description={t`TV Preview shows your TV app on Interhost's TV lab. Contact Interhost support to have it switched on.`} />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="tvlab" data-cinema={cinema ? 'on' : 'off'}>
      <div>
        {back}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">{t`TV Preview`}</h1>
            <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t`TV lab runtimes and browser previews. Available controls and video depend on the selected device. Emulator and browser results do not replace testing on real TVs.`}</p>
            <TenantAnalyticsLink />
          </div>
          <LeaseBar tenant={tenant.slug} lease={st.lease ?? null} operator={!!st.operator} canDrive={canDrive} idleMin={Math.round((st.lease_idle_s ?? 600) / 60)} />
        </div>
      </div>
      {st.operator ? <TVLabSetup /> : null}
      {st.connected === false ? <p className="rounded-md border border-warn/30 bg-warn/10 p-3 text-sm text-warn" role="status">{t`The TV lab does not answer right now. The page keeps trying.`}</p> : null}
      {devices.length === 0 ? (
        st.connected === false ? null : <EmptyState title={t`No devices in the TV lab yet`} />
      ) : (
        <div ref={cinemaArea} className={cinema ? 'grid scroll-mt-16 gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]' : 'grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]'}
          onKeyDownCapture={(e) => {
            if (!cinema || e.key !== 'Escape' || isLabEditingKey(e.nativeEvent) || document.querySelector('[role="dialog"]')) return;
            e.preventDefault(); e.stopPropagation(); exitCinema();
          }}>
          <div className={cinema ? 'sticky top-14 z-10 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-surface p-3 lg:col-span-2' : 'hidden'} data-testid="tvlab-cinema-toolbar">
            <div>
              <h2 className="text-sm font-semibold">{t`Cinema view`}</h2>
              <p className="text-xs text-fg-muted">{t`Focus the screen to use the remote. Esc exits cinema; Backspace sends Back to the TV.`}</p>
            </div>
            <Button size="sm" variant="secondary" onClick={exitCinema} data-testid="tvlab-cinema-exit"><Minimize2 className="size-4" aria-hidden /> {t`Exit cinema`}</Button>
          </div>
          <div className={cinema ? 'min-w-0 content-start' : 'grid content-start gap-3 sm:grid-cols-2'} role="list" aria-label={t`Devices`}>
            {devices.map((d) => <DeviceTile key={d.id} tenant={tenant.slug} d={d} selected={d.id === sel?.id} driving={mine} live={d.id === sel?.id && d.id !== fsId} sound={sound}
              cinema={cinema} previewRef={d.id === sel?.id ? previewRef : undefined}
              onSelect={() => setSelId(d.id)} onFullScreen={() => { setSelId(d.id); setFsId(d.id); }} />)}
          </div>
          {sel ? (
            <div className="min-w-0 space-y-4">
              <Controls tenant={tenant.slug} d={sel} driving={mine} sound={sound} onSound={setSound} onFullScreen={() => setFsId(sel.id)} cinema={cinema} onCinema={enterCinema} keyboardScope={cinemaArea} />
              <div className={cinema ? 'hidden' : undefined}><Files tenant={tenant.slug} /></div>
            </div>
          ) : null}
          {sel ? <div className={cinema ? 'hidden' : 'xl:col-span-2'}><Logs key={sel.id} d={sel} /></div> : null}
        </div>
      )}
      {fs ? <FullScreen tenant={tenant.slug} d={fs} driving={mine} sound={sound} onSound={setSound} onClose={() => setFsId(null)} /> : null}
    </div>
  );
}

function LeaseBar({ tenant, lease, operator, canDrive, idleMin }: {
  tenant: string; lease: { name: string; tenant: string; mine: boolean; own_tenant?: boolean } | null; operator: boolean; canDrive: boolean; idleMin: number;
}) {
  const { t } = useLingui();
  const toast = useToast();
  const { take, release } = useTVLabLease(tenant);
  const doTake = (force: boolean) => take.mutate(force, { onError: (e) => toast.push({ title: problemText(e), tone: 'danger' }) });
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2" data-testid="tvlab-lease" aria-live="polite">
      {lease?.mine ? (
        <>
          <Badge tone="ok"><Hand className="size-3" aria-hidden /> {t`You control the TV lab`}</Badge>
          <span className="text-xs text-fg-muted">{t`Released after ${idleMin} minutes without an action`}</span>
          <Button size="sm" variant="secondary" onClick={() => release.mutate()} loading={release.isPending}><LogOut className="size-4 icon-dir" aria-hidden /> {t`Release control`}</Button>
        </>
      ) : lease && lease.own_tenant === false && !operator ? (
        <>
          <Badge tone="warn"><Hand className="size-3" aria-hidden /> {t`Another organisation is using the TV lab`}</Badge>
          <span className="text-xs text-fg-muted">{t`The screens show again when you take control after it is released`}</span>
        </>
      ) : lease ? (
        <>
          <Badge tone="warn"><Hand className="size-3" aria-hidden /> {t`Controlled by ${lease.name}`}</Badge>
          <span className="text-xs text-fg-muted">{t`You are watching`}</span>
          {operator && canDrive ? <Button size="sm" variant="secondary" onClick={() => doTake(true)} loading={take.isPending}>{t`Take over`}</Button> : null}
        </>
      ) : (
        <>
          <Badge>{t`Nobody controls the TV lab`}</Badge>
          {canDrive ? <Button size="sm" onClick={() => doTake(false)} loading={take.isPending} data-testid="tvlab-take"><Hand className="size-4" aria-hidden /> {t`Take control`}</Button> : null}
        </>
      )}
    </div>
  );
}

function fmtMs(ms: number) { return (ms / 1000).toFixed(ms < 10_000 ? 2 : 1); }

/** A click on the screen picture of the device you drive = a mouse click on the TV (Android tap, Chrome click, the
 *  Samsung emulator's window). Returns the img props, or {} when clicks only select. */
function useTapScreen(tenant: string, d: TVLabDevice, on: boolean) {
  const { t } = useLingui();
  const toast = useToast();
  const act = useTVLabAction(tenant);
  if (!on || !supportsLabAction(d, 'tap')) return {};
  return {
    className: 'cursor-pointer',
    onClick: (e: React.MouseEvent<HTMLImageElement | HTMLVideoElement>) => {
      e.stopPropagation();
      const p = tapPoint(e);
      if (p) act.mutate({ id: d.id, a: { action: 'tap', ...p } }, { onError: (err) => toast.push({ title: labActionText(err, t`Our app isn't installed on this TV yet, so it can't take this now`), tone: 'danger' }) });
    },
  };
}

/** A failed key / click: the lab's reason, or `noApp` when our app isn't running on the device (the Samsung emulator
 *  until the certificate exists: only arrows, OK, Back and digits reach the TV then). */
function labActionText(e: unknown, noApp: string): string {
  const x = e as ApiError;
  return x?.status === 409 && /our app is not running/.test(x.problem?.detail ?? '') ? noApp : problemText(e);
}

function DeviceTile({ tenant, d, selected, driving, live, sound, onSelect, onFullScreen, cinema, previewRef }: { tenant: string; d: TVLabDevice; selected: boolean; driving: boolean; live: boolean; sound: boolean; onSelect: () => void; onFullScreen: () => void; cinema: boolean; previewRef?: React.Ref<HTMLButtonElement> }) {
  const { t } = useLingui();
  const tap = useTapScreen(tenant, d, selected && driving);
  const [hidden, setHidden] = React.useState(typeof document !== 'undefined' && document.hidden);
  // frames stop while the tab is hidden and resume when it is shown again
  React.useEffect(() => {
    const vis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', vis);
    return () => document.removeEventListener('visibilitychange', vis);
  }, []);
  const status = d.status === 'online' ? { tone: 'ok' as const, label: t`Online` } : d.status === 'setup' ? { tone: 'warn' as const, label: t`Setup required` } : d.status === 'offline' ? { tone: 'danger' as const, label: t`Offline` } : d.status === 'error' ? { tone: 'danger' as const, label: t`Error` } : { tone: 'neutral' as const, label: t`Starting` };
  return (
    <div role="listitem" className={cinema && !selected ? 'hidden' : undefined}>
      <button ref={previewRef} type="button" onClick={onSelect} onDoubleClick={cinema || tap.onClick ? undefined : onFullScreen} title={tap.onClick ? t`Click the screen = a mouse click on the TV` : cinema ? undefined : t`Double-click for full screen`} aria-pressed={selected} data-testid={`tvlab-device-${d.id}`} data-tvlab-preview={cinema && selected ? 'true' : undefined}
        className={`block w-full overflow-hidden rounded-lg border bg-surface text-start transition-colors ${selected ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-fg-muted'}`}>
        <div className={`relative bg-black ${cinema ? 'max-h-[calc(100dvh-12rem)]' : ''}`} style={{ aspectRatio: `${d.width || 16} / ${d.height || 9}` }}>
          <ScreenView tenant={tenant} d={d} live={live} paused={hidden} sound={sound} onClick={tap.onClick} className={`absolute inset-0 size-full object-contain ${tap.className ?? ''}`} />
          {d.recording ? <span className="absolute start-2 top-2 inline-flex items-center gap-1 rounded bg-black/70 px-2 py-0.5 text-xs text-white"><Circle className="size-3 fill-current text-danger" aria-hidden /> {t`Recording`}</span> : null}
          {d.setup ? (
            <span className="absolute start-2 end-2 bottom-2 rounded bg-black/80 px-2 py-1 text-xs text-white" data-testid={`tvlab-setup-${d.id}`}>
              {d.setup === 'samsung-certificate' ? t`Our app is not installed yet: this emulator needs a Samsung certificate. See "Lab setup" above.`
                : d.setup === 'roku-cloud-account' ? t`Roku Cloud Emulator needs an operator-provided developer account and cloud session.`
                : d.setup === 'roku-cloud-stopped' ? t`The Roku cloud session is stopped. An operator must start it before viewing.`
                : d.setup === 'roku-cloud-input' ? t`Roku video is available; remote input still needs operator setup.`
                : d.setup === 'vidaa-sdk-access' ? t`VIDAA SDK access is not configured. This target is unavailable until vendor tooling is verified.`
                : t`This device still needs setup. See "Lab setup" above.`}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <span className="min-w-0 truncate text-sm font-medium"><bdi>{d.label}</bdi></span>
          <span className="flex items-center gap-2 text-xs text-fg-muted">
            {d.runtime ? <Badge tone="neutral" data-testid={`tvlab-runtime-${d.id}`}>{d.runtime === 'chromium-preview' ? t`Chromium preview` : d.runtime === 'roku-cloud' ? t`Roku Cloud Emulator` : t`VIDAA SDK target`}</Badge> : null}
            {d.app ? <Badge tone="neutral" data-testid={`tvlab-app-${d.id}`}>{d.app === 'native' ? t`Native app` : t`Web app`}</Badge> : null}
            {d.firstFrameMs != null ? <span title={t`Launch → first frame`} className="font-medium text-fg">{t`First frame ${fmtMs(d.firstFrameMs)} s`}</span> : null}
            <Badge tone={status.tone}>{status.label}</Badge>
          </span>
        </div>
      </button>
    </div>
  );
}

/** A device's screen: live video with sound when `live` (WebRTC, useLiveVideo), else, and until it connects or when
 *  it can't, the screen pictures. Pictures stop while the live video plays (they cost the lab CPU). */
function ScreenView({ tenant, d, live, paused, sound, onClick, className }: {
  tenant: string; d: TVLabDevice; live: boolean; paused: boolean; sound: boolean; onClick?: (e: React.MouseEvent<HTMLImageElement | HTMLVideoElement>) => void; className: string;
}) {
  const { t } = useLingui();
  const transport = labView(d);
  const [tabHidden, setTabHidden] = React.useState(document.hidden);
  React.useEffect(() => {
    const changed = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, []);
  const inactive = paused || tabHidden;
  const whep = useLiveVideo(d.id, live && !inactive && transport === 'whep');
  const vendor = useVendorVideo(d.id, live && !inactive && transport === 'janus', tenant);
  const v = transport === 'janus' ? vendor : whep;
  const playing = v.state === 'live' && !!v.stream;
  const src = useScreen(d.id, inactive || playing || (transport !== 'whep' && transport !== 'frames'));
  const ref = React.useRef<HTMLVideoElement>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || !v.stream) return;
    if (el.srcObject !== v.stream) el.srcObject = v.stream;
    el.muted = !sound;
    void el.play().catch(() => { el.muted = true; void el.play().catch(() => {}); }); // sound needs a click on the page first
  }, [v.stream, sound]);
  return (
    <>
      {playing ? (
        <video ref={ref} autoPlay playsInline muted onClick={onClick} aria-label={t`Live video of ${d.label}`} className={className} />
      ) : src ? (
        // the click is a mouse click on the TV; the keyboard drives the TV through the remote's shortcuts (Controls)
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <img src={src} alt={t`Screen of ${d.label}`} onClick={onClick} className={className} />
      ) : null}
      {live && (transport === 'whep' || transport === 'janus') ? (
        <span className="pointer-events-none absolute bottom-2 start-2 inline-flex items-center gap-1 rounded bg-black/70 px-2 py-0.5 text-xs text-white" title={v.state === 'failed' ? transport === 'janus' ? t`The vendor video connection is unavailable.` : t`Live video can't connect from this network (UDP or TCP port 8189 to the TV lab); showing screen pictures` : undefined}>
          <Radio className={`size-3 ${playing ? 'text-danger' : ''}`} aria-hidden />
          {playing ? t`Live` : v.state === 'failed' ? transport === 'janus' ? t`Video unavailable` : t`Pictures only` : t`Connecting…`}
        </span>
      ) : null}
    </>
  );
}

function SoundButton({ sound, onSound, className }: { sound: boolean; onSound: (on: boolean) => void; className?: string }) {
  const { t } = useLingui();
  return (
    <Button size="sm" variant="ghost" className={className} onClick={() => onSound(!sound)} aria-pressed={sound} data-testid="tvlab-sound">
      {sound ? <Volume2 className="size-4" aria-hidden /> : <VolumeX className="size-4" aria-hidden />} {sound ? t`Sound on` : t`Sound off`}
    </Button>
  );
}

function Controls({ tenant, d, driving, sound, onSound, onFullScreen, cinema, onCinema, keyboardScope }: { tenant: string; d: TVLabDevice; driving: boolean; sound: boolean; onSound: (on: boolean) => void; onFullScreen: () => void; cinema: boolean; onCinema: () => void; keyboardScope: React.RefObject<HTMLElement | null> }) {
  const { t } = useLingui();
  const toast = useToast();
  const act = useTVLabAction(tenant);
  const busyRef = React.useRef(false);
  const run = React.useCallback((a: Parameters<typeof act.mutate>[0]['a'], ok?: string) => {
    if (!driving || (a.action === 'key' ? !supportsLabKey(d, a.key) : !supportsLabAction(d, a.action))) return;
    act.mutate({ id: d.id, a }, {
      onSuccess: () => { if (ok) toast.push({ title: ok, tone: 'ok' }); },
      onError: (e) => toast.push({ title: problemText(e), tone: 'danger' }),
    });
  }, [act, d, driving, toast]);
  const press = React.useCallback((key: string) => {
    if (!driving || busyRef.current || !supportsLabKey(d, key)) return;
    busyRef.current = true;
    act.mutate({ id: d.id, a: { action: 'key', key } }, {
      onError: (e) => toast.push({ title: labActionText(e, t`Our app isn't installed on this TV yet: only arrows, OK, Back and digits work`), tone: 'danger' }),
      onSettled: () => { busyRef.current = false; },
    });
  }, [act, d, driving, toast, t]);

  // keyboard shortcuts while you drive (not inside form fields, not with modifiers)
  React.useEffect(() => {
    if (!driving) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.defaultPrevented || isLabEditingKey(e)) return;
      if (cinema && (!el || !keyboardScope.current?.contains(el) || !el.closest('[data-tvlab-preview="true"], [data-testid="tvlab-remote"]'))) return;
      if (el?.closest('button') && el.dataset['tvlabPreview'] !== 'true' && (e.key === 'Enter' || e.key === ' ')) return; // focused controls keep their native activation
      if (document.querySelector('[role="dialog"]')) return;
      const k = keyFromKeyboard(e);
      if (!k || !supportsLabKey(d, k)) return;
      e.preventDefault();
      press(k);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [driving, press, cinema, keyboardScope, d]);

  const label: Record<string, string> = {
    up: t`Up`, down: t`Down`, left: t`Left`, right: t`Right`, ok: t`OK`, back: t`Back`, home: t`Home`,
    playpause: t`Play / pause`, play: t`Play`, pause: t`Pause`, stop: t`Stop`, ff: t`Fast forward`, rw: t`Rewind`,
    red: t`Red`, green: t`Green`, yellow: t`Yellow`, blue: t`Blue`, volup: t`Volume up`, voldown: t`Volume down`, info: d.runtime === 'roku-cloud' ? t`Options` : t`Info`, replay: t`Instant replay`,
  };
  const media: Record<string, React.ReactNode> = {
    rw: <Rewind className="size-4" aria-hidden />, ff: <FastForward className="size-4" aria-hidden />, stop: <Square className="size-4" aria-hidden />,
    play: <Play className="size-4" aria-hidden />, pause: <Pause className="size-4" aria-hidden />,
    playpause: <span className="flex"><Play className="size-4" aria-hidden /><Pause className="size-4" aria-hidden /></span>,
  };
  // a render helper, not a component: remote buttons keep their identity (and focus) across polling re-renders
  const K = (k: string, children: React.ReactNode, className?: string) => (
    <RemoteKey key={k} k={k} label={label[k] ?? k} driving={driving && supportsLabKey(d, k)} press={press} className={className}>{children}</RemoteKey>
  );
  const recording = !!d.recording;
  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface p-3" aria-label={t`Remote for ${d.label}`} data-testid="tvlab-remote">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="truncate text-sm font-semibold"><bdi>{d.label}</bdi></h2>
        <span className="flex flex-wrap items-center gap-2">
          {!driving ? <span className="text-xs text-fg-muted">{t`Take control to use the remote`}</span> : null}
          <SoundButton sound={sound} onSound={onSound} />
          <Button size="sm" variant="secondary" onClick={onCinema} className={cinema ? 'hidden' : undefined} data-testid="tvlab-cinema-enter">
            <Maximize2 className="size-4" aria-hidden /> {t`Cinema view`}
          </Button>
          <Button size="sm" variant="ghost" onClick={onFullScreen} className={cinema ? 'hidden' : undefined} data-testid="tvlab-fullscreen">
            <Maximize2 className="size-4" aria-hidden /> {t`Full screen`}
          </Button>
        </span>
      </div>
      {/* the remote is a physical object: its arrows keep their directions in Hebrew */}
      <div dir="ltr" className="mx-auto grid w-48 grid-cols-3 gap-1">
        <span />
        {K('up', <ChevronUp className="size-5" aria-hidden />)}
        <span />
        {K('left', <ChevronLeft className="size-5" aria-hidden />)}
        {K('ok', 'OK', 'font-semibold')}
        {K('right', <ChevronRight className="size-5" aria-hidden />)}
        {K('back', <Undo2 className="size-4" aria-hidden />)}
        {K('down', <ChevronDown className="size-5" aria-hidden />)}
        {(d.capabilities ? supportsLabKey(d, 'home') : hasHomeKey(d.kind)) ? K('home', <Home className="size-4" aria-hidden />) : <span />}
      </div>
      <div dir="ltr" className="flex flex-wrap justify-center gap-1">
        {(d.capabilities ? ['rw', 'playpause', 'play', 'pause', 'ff', 'stop'].filter((k) => supportsLabKey(d, k)) : mediaKeys(d.kind)).map((k) => K(k, media[k]))}
        {(d.capabilities ? supportsLabKey(d, 'voldown') || supportsLabKey(d, 'volup') : hasVolumeKeys(d.kind)) ? <>{K('voldown', <Volume1 className="size-4" aria-hidden />)}{K('volup', <Volume2 className="size-4" aria-hidden />)}</> : null}
        {d.capabilities?.keys.includes('info') ? K('info', label['info']) : null}
        {d.capabilities?.keys.includes('replay') ? K('replay', t`Instant replay`) : null}
      </div>
      <div dir="ltr" className="flex justify-center gap-1">
        {COLOUR_KEYS.filter((k) => supportsLabKey(d, k)).map((k) => K(k, <span className="block h-3 w-6 rounded-sm" style={{ background: COLOUR_HEX[k] }} aria-hidden />))}
      </div>
      <div dir="ltr" className="mx-auto grid w-48 grid-cols-5 gap-1">
        {DIGIT_KEYS.filter((k) => supportsLabKey(d, k)).map((k) => K(k, k.slice(1)))}
      </div>
      <p className="text-xs text-fg-muted">{d.capabilities ? t`Keyboard shortcuts send only keys supported by this device. In cinema, Esc closes the view and Backspace sends Back.` : cinema ? t`Keyboard: arrows, Enter = OK, Backspace = Back, Esc exits cinema, Space = play/pause, 0–9, F1–F4 = colour keys.` : t`Keyboard: arrows, Enter = OK, Backspace or Esc = Back, Space = play/pause, 0–9, F1–F4 = colour keys.`}</p>
      <div className="flex flex-wrap gap-2 border-t border-border pt-3">
        {d.apps && d.apps.length > 1 ? (
          <span className="flex items-center gap-1" role="group" aria-label={t`App on this TV`} data-testid="tvlab-app-switch">
            {d.apps.map((a) => (
              <Button key={a} size="sm" variant={(d.app ?? 'web') === a ? 'primary' : 'secondary'} aria-pressed={(d.app ?? 'web') === a}
                disabled={!driving || act.isPending || !supportsLabAction(d, 'launch') || (d.app ?? 'web') === a}
                onClick={() => run({ action: 'launch', app: a }, a === 'native' ? t`Native app started on this TV` : t`Web app started on this TV`)} data-testid={`tvlab-app-${a}`}>
                {a === 'native' ? t`Native app` : t`Web app`}
              </Button>
            ))}
          </span>
        ) : null}
        <Button size="sm" variant="secondary" disabled={!driving || act.isPending || !supportsLabAction(d, 'launch')} onClick={() => run(d.app ? { action: 'launch', app: d.app } : { action: 'launch' }, t`App relaunched — the first-frame timer restarted`)} data-testid="tvlab-launch">
          <RotateCw className="size-4" aria-hidden /> {t`Relaunch app`}
        </Button>
        <Button size="sm" variant="secondary" disabled={!driving || act.isPending || !supportsLabAction(d, 'screenshot')} onClick={() => run({ action: 'screenshot' }, t`Screenshot saved`)} data-testid="tvlab-shot">
          <Camera className="size-4" aria-hidden /> {t`Screenshot`}
        </Button>
        <Button size="sm" variant={recording ? 'danger' : 'secondary'} disabled={!driving || act.isPending || !supportsLabAction(d, 'record')} onClick={() => run({ action: 'record', on: !recording }, recording ? t`Recording saved` : undefined)} data-testid="tvlab-record">
          {recording ? <Square className="size-4" aria-hidden /> : <Circle className="size-4" aria-hidden />} {recording ? t`Stop recording` : t`Record`}
        </Button>
      </div>
    </section>
  );
}

const COLOUR_HEX: Record<string, string> = { red: '#e53935', green: '#43a047', yellow: '#fdd835', blue: '#1e88e5' };

/** One remote button (a stable component, so polling re-renders keep the keyboard focus on it). */
function RemoteKey({ k, label, driving, press, className, children }: {
  k: string; label: string; driving: boolean; press: (k: string) => void; className?: string | undefined; children: React.ReactNode;
}) {
  return (
    <Button type="button" size="sm" variant="secondary" disabled={!driving} onClick={() => press(k)} aria-label={label} title={label} className={className} data-testid={`tvlab-key-${k}`}>{children}</Button>
  );
}

function Files({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const files = useTVLabFiles(tenant, true);
  const items = files.data?.items ?? [];
  return (
    <section className="rounded-lg border border-border bg-surface p-3" aria-label={t`Screenshots and recordings`} data-testid="tvlab-files">
      <h2 className="mb-2 text-sm font-semibold">{t`Screenshots and recordings`}</h2>
      {files.isPending ? <Skeleton className="h-16" /> : items.length === 0 ? <p className="text-sm text-fg-muted">{t`Nothing saved yet.`}</p> : (
        <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
          {items.map((f) => (
            <li key={f.kind + f.name} className="flex items-center justify-between gap-2">
              <a href={fileUrl(f)} target="_blank" rel="noreferrer" className="min-w-0 truncate text-primary underline-offset-4 hover:underline">
                {f.kind === 'shots' ? <Camera className="me-1 inline size-3" aria-hidden /> : <Circle className="me-1 inline size-3" aria-hidden />}
                <bdi className="ltr-isolate">{f.name}</bdi>
              </a>
              <a href={fileUrl(f, true)} aria-label={t`Download ${f.name}`} className="shrink-0 text-fg-muted hover:text-fg"><Download className="size-4" aria-hidden /></a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Logs({ d }: { d: TVLabDevice }) {
  const { t } = useLingui();
  const [lines, setLines] = React.useState<TVLabLogLine[]>([]);
  const [paused, setPaused] = React.useState(false);
  const [err, setErr] = React.useState(false);
  const box = React.useRef<HTMLPreElement>(null);
  React.useEffect(() => {
    if (paused) return;
    let since = lines.length ? lines[lines.length - 1]!.n : 0;
    let stop = false;
    const tick = async () => {
      try {
        const add = await fetchTVLabLogs(d.id, since);
        if (stop) return;
        setErr(false);
        if (add.length) {
          since = add[add.length - 1]!.n;
          setLines((old) => [...old, ...add].slice(-500));
        }
      } catch { if (!stop) setErr(true); }
    };
    void tick();
    const iv = window.setInterval(() => void tick(), 1500);
    return () => { stop = true; window.clearInterval(iv); };
  }, [d.id, paused]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { const el = box.current; if (el) el.scrollTop = el.scrollHeight; }, [lines]);
  return (
    <section className="rounded-lg border border-border bg-surface p-3" aria-label={t`Log of ${d.label}`} data-testid="tvlab-logs">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t`Log`} · <bdi>{d.label}</bdi></h2>
        <div className="flex items-center gap-2">
          {err ? <Badge tone="warn">{t`Log unavailable`}</Badge> : null}
          <Button size="sm" variant="ghost" onClick={() => setPaused((p) => !p)}>{paused ? t`Resume` : t`Pause`}</Button>
          <Button size="sm" variant="ghost" onClick={() => setLines([])}>{t`Clear`}</Button>
        </div>
      </div>
      <pre ref={box} dir="ltr" className="h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-black/90 p-2 text-start font-mono text-xs leading-5 text-green-200">
        {lines.length ? lines.map((l) => `${new Date(l.t).toLocaleTimeString('en-GB')}  ${l.line}`).join('\n') : t`No log lines yet.`}
      </pre>
    </section>
  );
}

/** One device's screen filling the monitor (browser full screen; a fixed overlay where that is refused). The remote's
 *  keyboard shortcuts keep working (Controls stays mounted, its listener is on window). With Keyboard Lock (Chrome,
 *  Edge) Esc is the TV's Back and holding Esc leaves full screen; without it (Firefox, Safari) Esc leaves full screen
 *  and Back is Backspace. */
function FullScreen({ tenant, d, driving, sound, onSound, onClose }: { tenant: string; d: TVLabDevice; driving: boolean; sound: boolean; onSound: (on: boolean) => void; onClose: () => void }) {
  const { t } = useLingui();
  const tap = useTapScreen(tenant, d, driving);
  const ref = React.useRef<HTMLDivElement>(null);
  const locked = useLabFullScreen(ref, onClose);
  return (
    <div ref={ref} tabIndex={-1} className="fixed inset-0 z-50 flex items-center justify-center bg-black outline-none" aria-label={t`Screen of ${d.label}`} data-testid="tvlab-fullscreen-view" data-keyboard-lock={locked ? 'on' : 'off'}>
      <ScreenView tenant={tenant} d={d} live paused={false} sound={sound} onClick={tap.onClick} className={`size-full object-contain ${tap.className ?? ''}`} />
      <div className="absolute end-3 top-3 flex items-center gap-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
        <bdi>{d.label}</bdi>
        <SoundButton sound={sound} onSound={onSound} className="text-white hover:bg-white/20" />
        <span className="opacity-70">{!driving ? t`Take control to use the remote` : locked ? t`Keyboard and mouse drive the TV · Esc or Backspace = Back · hold Esc to exit full screen` : t`Keyboard and mouse drive the TV · Backspace = Back · Esc exits full screen`}</span>
        <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={onClose} data-testid="tvlab-fullscreen-exit">
          <Minimize2 className="size-4" aria-hidden /> {t`Exit full screen`}
        </Button>
      </div>
    </div>
  );
}
